import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Calendar as CalendarIcon, Clock, MapPin, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import LoadingSpinner from '../components/LoadingSpinner';
import { toast } from 'sonner';

const EventosPage = () => {
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEventos();
  }, []);

  const fetchEventos = async () => {
    try {
      const eventosQuery = query(
        collection(db, 'eventos'),
        orderBy('fecha', 'asc')
      );
      const eventosSnapshot = await getDocs(eventosQuery);
      const eventosData = eventosSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        fecha: doc.data().fecha?.toDate()
      }));

      setEventos(eventosData);
    } catch (error) {
      console.error('Error fetching eventos:', error);
      toast.error('Error al cargar los eventos');
    } finally {
      setLoading(false);
    }
  };

  const addToCalendar = (evento) => {
    const startDate = format(evento.fecha, "yyyyMMdd'T'HHmmss");
    const endDate = format(new Date(evento.fecha.getTime() + 2 * 60 * 60 * 1000), "yyyyMMdd'T'HHmmss");
    
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(evento.titulo)}&dates=${startDate}/${endDate}&details=${encodeURIComponent(evento.descripcion)}&location=${encodeURIComponent(evento.ubicacion || 'Centro Comercial La Convención')}`;
    
    window.open(googleCalendarUrl, '_blank');
    toast.success('Evento añadido al calendario');
  };

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
              Calendario de <span className="gradient-text">Eventos</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              No te pierdas nuestros eventos especiales, promociones y actividades
            </p>
          </motion.div>

          {eventos.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <CalendarIcon className="mx-auto mb-4 text-muted-foreground" size={64} />
              <h3 className="text-2xl font-heading font-semibold mb-2">No hay eventos programados</h3>
              <p className="text-muted-foreground">Vuelve pronto para ver nuestros próximos eventos</p>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {eventos.map((evento, index) => (
                <motion.div
                  key={evento.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                    <div className="relative h-56 overflow-hidden">
                      <img
                        src={evento.imagen}
                        alt={evento.titulo}
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute top-4 left-4 bg-primary text-primary-foreground px-4 py-2 rounded-lg">
                        <div className="text-2xl font-bold">
                          {format(evento.fecha, 'd', { locale: es })}
                        </div>
                        <div className="text-sm">
                          {format(evento.fecha, 'MMM', { locale: es })}
                        </div>
                      </div>
                    </div>
                    <CardContent className="p-6 flex-grow flex flex-col">
                      <h3 className="text-xl font-heading font-semibold mb-3">
                        {evento.titulo}
                      </h3>
                      <p className="text-muted-foreground mb-4 flex-grow">
                        {evento.descripcion}
                      </p>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Clock size={16} />
                          <span>
                            {format(evento.fecha, "EEEE, d 'de' MMMM 'a las' HH:mm", { locale: es })}
                          </span>
                        </div>
                        {evento.ubicacion && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <MapPin size={16} />
                            <span>{evento.ubicacion}</span>
                          </div>
                        )}
                      </div>

                      <Button
                        onClick={() => addToCalendar(evento)}
                        variant="outline"
                        className="w-full mt-auto border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                      >
                        <Plus className="mr-2" size={18} />
                        Agregar al Calendario
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default EventosPage;
